import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Route, withRouter } from 'react-router-dom';
import { inject, observer } from 'mobx-react';

import SceneChangerContext from './sceneChangerContext';
import Link from './components/SceneChangerLink';
import Header from '../Header/';

export const SceneChangerLink = Link;

const Root = styled.div``;

const Scenes = styled.div``;

const SceneContainer = styled.div`
  position: relative;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  will-change: opacity;

  ${props =>
    props.animationRunning &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      opacity: 0;
    `};
`;

class SceneChanger extends React.Component {
  static propTypes = {
    routes: PropTypes.array.isRequired,
    initialSceneNode: PropTypes.object,
    sceneChangerStore: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    fromTransition: PropTypes.bool,
    progressStore: PropTypes.object.isRequired,
    menuStore: PropTypes.object.isRequired,
    scrollControllerStore: PropTypes.object.isRequired,
    themeStore: PropTypes.object.isRequired,
    onChange: PropTypes.func,
  };

  state = {
    scenes: [],
  };

  sceneNodes = [];

  componentWillMount() {
    if (this.props.fromTransition) {
      return;
    }

    const route = this.props.sceneChangerStore.getRoute(this.props.routes, this.props.location.pathname);

    const scenes = [
      {
        component: route.component,
        location: {
          pathname: this.props.location.pathname,
          search: this.props.location.search,
        },
        route: route,
        props: {},
      },
    ];
    this.setState({ scenes });
  }

  componentDidMount() {
    this.props.onChange && this.props.onChange(this.props.location);
    this.props.sceneChangerStore.setSceneChangerInstance(this);
    this.props.sceneChangerStore.init();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.location !== nextProps.location) {
      this.props.sceneChangerStore.init(this);

      const route = this.props.sceneChangerStore.getRoute(this.props.routes, nextProps.location.pathname);

      const scenes = [
        {
          component: route.component,
          location: {
            pathname: nextProps.location.pathname,
            search: nextProps.location.search,
          },
          route,
          props: {},
        },
      ];
      setTimeout(() => {
        this.setState({ scenes }, () => {
          TweenMax.set(this.sceneNodes[this.sceneNodes.length - 1], { alpha: 1 });
          this.props.scrollControllerStore.unlockWithScrollbar();
        });
      }, 0);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const prevLastScene = prevState.scenes[prevState.scenes.length - 1];
    const lastScene = this.state.scenes[this.state.scenes.length - 1];
    if (prevLastScene && prevLastScene.location.pathname !== lastScene.location.pathname) {
      this.props.onChange && this.props.onChange(lastScene.location);
    }
  }

  handleLinkClick(to) {
    this.props.progressStore.turnOn();
    this.props.themeStore.resetInvertingBlock();

    const route = this.props.sceneChangerStore.getRoute(this.props.routes, to);
    this.props.scrollControllerStore.lockWithScrollbar();

    const [pathname, search] = to.split('?');

    const nextSceneData = {
      component: route.component,
      location: {
        pathname,
        search: search ? `?${search}` : '',
      },
      route,
      props: {
        withTransition: true,
        animationRunning: true,
      },
    };

    this.setState(prevState => ({
      scenes: [...prevState.scenes, nextSceneData],
    }));
  }

  updateScenes = () =>
    new Promise(resolve => {
      this.setState(
        prevState => {
          const scenes = [...prevState.scenes];
          if (scenes.length < 2) {
            return;
          }
          scenes[scenes.length - 2].inactive = true;
          return {
            scenes,
          };
        },
        () => {
          this.props.menuStore.hide();
          resolve();
        }
      );
    });

  handleAnimationEnd(fromAnimation) {
    const animateNextPageShowing = () =>
      TweenMax.to(this.sceneNodes[this.sceneNodes.length - 1], 0.65, {
        alpha: 1,
        ease: Power1.easeOut,
        onComplete: () => {
          this.setState(prevState => {
            const scenes = [...prevState.scenes];
            scenes[scenes.length - 1].props.animationRunning = false;
            return {
              scenes,
            };
          });
        },
      });

    switch (fromAnimation.type) {
      case 'common':
        this.props.sceneChangerStore.setShouldHandleColors();
        TweenMax.set(this.sceneNodes[this.sceneNodes.length - 1], { alpha: 0 });
        TweenMax.to(this.sceneNodes[this.sceneNodes.length - 2], 0.25, {
          alpha: 0,
          ease: Power1.easeIn,
          onComplete: () => {
            this.updateScenes().then(() => {
              window.scrollTo(0, 0);
              this.props.progressStore.addMediaLoadedListener(() => {
                this.props.scrollControllerStore.unlockWithScrollbar();
                this.props.progressStore.turnOff();
                animateNextPageShowing();
              });
            });
          },
        });
        break;
      case 'nextBlock':
        this.props.progressStore.turnOff();
        this.props.scrollControllerStore.unlockWithScrollbar();
        this.updateScenes().then(() => {
          window.scrollTo(0, 0);
          animateNextPageShowing();
        });
        break;
      case 'previewBig':
        this.props.progressStore.turnOff();
        this.props.scrollControllerStore.unlockWithScrollbar();
        window.scrollTo(0, 0);
        TweenMax.set(this.sceneNodes[this.sceneNodes.length - 1], { alpha: 1 });
        TweenMax.to(this.sceneNodes[this.sceneNodes.length - 2], 0.35, {
          alpha: 0,
          ease: Power1.easeIn,
          onComplete: () => {
            this.updateScenes().then(() => {
              animateNextPageShowing();
            });
          },
        });
        break;
      default:
        console.error('Unknown transition type');
    }
  }

  render() {
    const currentSceneData = this.state.scenes[this.state.scenes.length - 1];

    return (
      <Root>
        <SceneChangerContext.Provider value={{ onLinkClick: to => this.handleLinkClick(to) }}>
          <Header location={currentSceneData.location} />
          <Scenes>
            {this.state.scenes.map((sceneData, index) => {
              const SceneComponent = sceneData.component;
              return (
                <SceneContainer
                  key={index}
                  innerRef={node => this.sceneNodes.push(node)}
                  animationRunning={sceneData.props.animationRunning}
                  data-scene-container
                >
                  {!sceneData.inactive && (
                    <Route
                      location={sceneData.location}
                      path={sceneData.route.path ? sceneData.route.path.toString() : null}
                    >
                      <SceneComponent {...sceneData.props} />
                    </Route>
                  )}
                </SceneContainer>
              );
            })}
          </Scenes>
        </SceneChangerContext.Provider>
      </Root>
    );
  }
}

export default withRouter(
  inject(stores => {
    return {
      sceneChangerStore: stores.store.sceneChanger,
      progressStore: stores.store.progress,
      menuStore: stores.store.menuStore,
      themeStore: stores.store.theme,
      scrollControllerStore: stores.store.scrollControllerStore,
    };
  })(observer(SceneChanger))
);



// WEBPACK FOOTER //
// ./src/components/SceneChanger/index.js